import { useEffect, useState } from 'react';
import { RewardsService, type TierLadderEntry } from '../../../../../services/rewards/RewardsService';

/**
 * Reads the enabled tier collection live, for the ladder rungs ABOVE the next
 * tier.
 *
 * Those rungs are display copy only and are deliberately NOT stored on the
 * machine: the next tier carries the per-machine component checks, and every rung
 * past it is just name + earn copy, which the live collection already has. So
 * this is fetched when the screen opens rather than persisted per machine.
 *
 * A failed read is not fatal — the screen still renders the assigned tier and the
 * next tier from the machine detail; the higher rungs simply do not appear.
 */
export function useTierLadder(enabled: boolean): {
  ladder: TierLadderEntry[];
  loading: boolean;
} {
  const [ladder, setLadder] = useState<TierLadderEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const entries = await new RewardsService().getTierLadder();
        if (!cancelled) setLadder(entries);
      } catch {
        // Higher rungs are optional; the rest of the screen still renders.
        if (!cancelled) setLadder([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { ladder, loading };
}
